import type { PerfectDrawProject, ProjectOrientation, ProjectType } from "./types";
import { createPanelTemplate } from "../comic/PanelTemplates";
import { isComicTemplateId, type ComicTemplateId } from "../comic/comicTypes";

export const PROJECT_SCHEMA_VERSION = 3;
export const MAX_PROJECT_NAME_LENGTH = 80;

export interface CanvasPreset {
  id: "square-1600" | "portrait-1600x2000" | "landscape-2000x1600";
  label: string;
  shortLabel: string;
  width: number;
  height: number;
  orientation: ProjectOrientation;
}

export const CANVAS_PRESETS: readonly CanvasPreset[] = [
  {
    id: "square-1600",
    label: "Square · 1600 × 1600",
    shortLabel: "1600 × 1600",
    width: 1600,
    height: 1600,
    orientation: "square",
  },
  {
    id: "portrait-1600x2000",
    label: "Portrait · 1600 × 2000",
    shortLabel: "1600 × 2000",
    width: 1600,
    height: 2000,
    orientation: "portrait",
  },
  {
    id: "landscape-2000x1600",
    label: "Landscape · 2000 × 1600",
    shortLabel: "2000 × 1600",
    width: 2000,
    height: 1600,
    orientation: "landscape",
  },
];

export interface NewProjectInput {
  name?: unknown;
  type?: unknown;
  orientation?: unknown;
  presetId?: unknown;
  comicTemplateId?: unknown;
}

function isProjectType(value: unknown): value is ProjectType {
  return value === "drawing" || value === "comic";
}

function isOrientation(value: unknown): value is ProjectOrientation {
  return value === "portrait" || value === "landscape" || value === "square";
}

export function getPresetForOrientation(orientation: ProjectOrientation): CanvasPreset {
  const preset = CANVAS_PRESETS.find((item) => item.orientation === orientation);
  if (!preset) throw new Error(`Missing canvas preset for ${orientation}`);
  return preset;
}

export function sanitizeProjectName(value: unknown, type: ProjectType): string {
  if (typeof value !== "string") return type === "comic" ? "Untitled Comic" : "Untitled Drawing";

  const cleaned = value
    .normalize("NFKC")
    .replace(/[\p{Cc}\p{Cf}]/gu, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, MAX_PROJECT_NAME_LENGTH)
    .trim();

  return cleaned || (type === "comic" ? "Untitled Comic" : "Untitled Drawing");
}

export function createProject(input: NewProjectInput = {}): PerfectDrawProject {
  const type: ProjectType = isProjectType(input.type) ? input.type : "drawing";
  const requestedPreset =
    typeof input.presetId === "string"
      ? CANVAS_PRESETS.find((preset) => preset.id === input.presetId)
      : undefined;
  const orientation: ProjectOrientation = isOrientation(input.orientation)
    ? input.orientation
    : (requestedPreset?.orientation ?? "square");
  const preset =
    requestedPreset?.orientation === orientation
      ? requestedPreset
      : getPresetForOrientation(orientation);
  const timestamp = new Date().toISOString();
  const layerId = crypto.randomUUID();
  const comicTemplateId: ComicTemplateId | undefined =
    type === "comic" && isComicTemplateId(input.comicTemplateId)
      ? input.comicTemplateId
      : type === "comic"
        ? "one-panel"
        : undefined;
  const dialogueLayerId = type === "comic" ? crypto.randomUUID() : undefined;
  const panels = comicTemplateId
    ? createPanelTemplate(comicTemplateId, preset.width, preset.height, orientation)
    : [];

  return {
    schemaVersion: PROJECT_SCHEMA_VERSION,
    id: crypto.randomUUID(),
    name: sanitizeProjectName(input.name, type),
    type,
    createdAt: timestamp,
    updatedAt: timestamp,
    width: preset.width,
    height: preset.height,
    orientation,
    comicTemplateId,
    activeLayerId: layerId,
    layers: [
      {
        id: layerId,
        name: "Sketch",
        type: "raster",
        role: "sketch",
        system: false,
        visible: true,
        locked: false,
        opacity: 1,
        order: 0,
      },
      ...(dialogueLayerId
        ? [
            {
              id: dialogueLayerId,
              name: "Dialogue",
              type: "object" as const,
              role: "dialogue" as const,
              system: true,
              visible: true,
              locked: false,
              opacity: 1,
              order: 1,
            },
          ]
        : []),
    ],
    panels,
    objects: [],
    settings: {
      backgroundColor: "#ffffff",
      easyMode: true,
      pressureEnabled: true,
      autosaveEnabled: true,
    },
  };
}
